import { useEventCompanyRole, type EventCompanyRoleState } from "@/lib/query/hooks/useEventEntitlements";
import { smsFieldsOfferedOf } from "@/lib/sms/useSmsFieldsOffered";
import { smsTextForGuest } from "@/lib/sms/smsGuestPreview";
import type { SmsEventValues } from "@/lib/sms/smsEventValues";
import { fillSmsEventMarkers } from "@/lib/utils/smsPlaceholders";
import { measureSms, SMS_MAX_PARTS } from "@/lib/utils/smsLength";
import { stableStringify } from "@/lib/utils/stable-stringify";
import { ACCOUNT_TEARDOWN_EVENT } from "@/lib/storage/accountScopedState";
import { parseFiltersApiRequest } from "@/lib/utils/parser";
import { pickSmsNumber } from "@/lib/utils/smsPhone";
import { smsSkipReasonKey, type SmsSkipReasonKey } from "@/lib/utils/smsSkipReason";
import { allMatchingFilterIsSafe } from "@/hooks/useBulkSelectionPayload";
import { countConditionFilters } from "@/components/ConditionBuilder/condition-utils";
import type { BulkSelection } from "@/types/BulkSelection";
import type { Capability } from "@/types/Capability";
import type { ConditionGroup } from "@/types/EventRule";
import type { Field } from "@/types/Field";
import type { Filter } from "@/types/Filter";
import type { Guest } from "@/types/Guest";
import type { SmsManualSelectionBody } from "@/types/SmsManualSend";
import type { SmsTemplate } from "@/types/SmsTemplate";

/**
 * SMS manual send from the guest list (plan ~/Desktop/veltric/sms-manual-send-work/plan.md
 * Rev. 4): the pure rules the dialog and its two entry points share — who
 * sees the entry (F-2), the selection body (B-1 = B-2), the cost line (M4),
 * what a refusal of the check / the submit means (F3-5 / F3-16) and the
 * submit key that survives a closed dialog (F3-2).
 */

export const SMS_MANUAL_TASK_TYPE = "SEND_SMS_MANUAL";
/** Most reachable recipients one send may have (M3, the backend refuses more). */
export const SMS_MANUAL_MAX_RECIPIENTS = 500;
/** Longest send name (B-1, as the e-mail send's). */
export const SMS_MANUAL_SEND_NAME_MAX = 100;
/** The client's patience with the submit (F3-16): after it, "status unclear". */
export const SMS_MANUAL_SUBMIT_TIMEOUT_MS = 30_000;

const SENDER_ROLES: ReadonlySet<string> = new Set(["OWNER", "ADMIN", "EDITOR"]);

// ── F-2: who sees the entry ─────────────────────────────────────────────────

export interface SmsManualGateInput {
  /** `smsFieldsOfferedOf`: the platform switch on, the event's SMS plugin on, not a placeholder event. */
  offered: boolean;
  can: (capability: Capability) => boolean;
  /** The raw EVENT role (`caps.role`, "CUSTOM" included). */
  eventRole: string | null | undefined;
  /** The caller in the event's COMPANY (`useEventCompanyRole`). */
  company: Pick<EventCompanyRoleState, "status" | "member" | "role">;
  archived: boolean;
  planLocked: boolean;
}

/**
 * May this caller send SMS by hand here (M1 / N1 / F3-5 / F3-14)? The SMS
 * offer, `guests:write` AND `workflows:write`, event role OWNER / ADMIN /
 * EDITOR (a CUSTOM role — PATs, AI, preview — never), and a member of the
 * event's company with company role OWNER / ADMIN / EDITOR; not on an
 * archived or plan-locked event. The company read: hidden while loading,
 * shown on error (the server decides and the dialog says why), shown when it
 * names a writer role.
 */
export function smsManualSendOf(input: SmsManualGateInput): boolean {
  if (!input.offered) return false;
  if (input.archived || input.planLocked) return false;
  if (!input.can("guests:write") || !input.can("workflows:write")) return false;
  if (typeof input.eventRole !== "string" || !SENDER_ROLES.has(input.eventRole)) return false;
  const c = input.company;
  if (c.status === "loading") return false;
  if (c.status === "error") return true;
  return c.member && c.role !== null && SENDER_ROLES.has(c.role);
}

/** What `useSmsManualSend` reads from the caller's capability and event providers. */
export interface SmsManualScope {
  caps: { can: (capability: Capability) => boolean; role?: string | null } & object;
  ctx: { event?: unknown; eventIsPlaceholder?: boolean } | null | undefined;
}

/**
 * `smsManualSendOf` from the providers the caller already reads (the guest
 * table, the guest drawer pass their `useCapabilities()` / `useEventContext()`).
 * The company role is read only once SMS is offered at all — an event without
 * the plugin asks the server nothing more.
 */
export function useSmsManualSend(eventId: string | null | undefined, { caps, ctx }: SmsManualScope): boolean {
  const offered = smsFieldsOfferedOf(caps, ctx as Parameters<typeof smsFieldsOfferedOf>[1]);
  const company = useEventCompanyRole(offered && eventId ? eventId : null);
  const event = (ctx?.event ?? null) as { archived?: unknown; planLocked?: unknown } | null;
  return smsManualSendOf({
    offered,
    can: caps.can,
    eventRole: caps.role,
    company,
    archived: event?.archived === true,
    planLocked: event?.planLocked === true,
  });
}

// ── The selection, spelled out once (B-1 = B-2, F3-3) ──────────────────────

/**
 * A manual SMS selection: the guest list's bulk selection, plus — only from
 * the SMS-Vorlagen picker (plan sms-like-email §2) — guests picked one by one
 * ON TOP of "all matching" a filter. `includeIds` rides a `filter` selection
 * only; the shared `BulkSelection` stays untouched (MED-4).
 */
export type SmsSelection = BulkSelection & { includeIds?: string[] };

/**
 * The fixed selection as the backend takes it: ticked ids ⇒ a whitelist,
 * "all matching" ⇒ the filters, the search and the un-ticked rows, with
 * `selectAll` saying it is meant (an empty filter list is then "every guest
 * of the list", never an accident). The picker's extra picks (`includeIds`)
 * are the whitelist beside the filters: the door then sends to
 * (filters ∧ ¬blacklist) ∨ (whitelist ∧ ¬blacklist) — main-backend
 * `GuestRepository.buildGuestMatchCondition`. A selection without them is
 * spelled exactly as before.
 */
export function smsManualSelectionBody(selection: SmsSelection): SmsManualSelectionBody {
  if (selection.mode === "ids") {
    return { whitelist: [...new Set(selection.ids)], blacklist: [], filters: [], selectAll: false };
  }
  const search = typeof selection.search === "string" ? selection.search.trim() : "";
  return {
    whitelist: Array.isArray(selection.includeIds) ? [...new Set(selection.includeIds)] : [],
    blacklist: [...new Set(selection.excludeIds)],
    filters: selection.filters,
    ...(search !== "" ? { bulkSearch: search } : {}),
    selectAll: selection.selectAll === true,
  };
}

// ── The SMS-Vorlagen picker: its lists → the selection (plan sms-like-email §2) ──

/** The picker's lists (`ListsProvider`): explicit picks, or "all" with picks on top and removals. */
export interface SmsPickerLists {
  allRecipients: boolean;
  whitelist: readonly string[];
  blacklist: readonly string[];
}

export type SmsPickerSelection =
  | { ok: true; selection: SmsSelection }
  /** `empty` = nobody picked; `filter` = "all" under a filter whose flat rows do not mean what the list shows. */
  | { ok: false; reason: "empty" | "filter" };

const sortedUnique = (ids: readonly string[]): string[] => [...new Set(ids)].sort();

/**
 * The selection the picker's lists stand for — the set its list and count
 * show, as the SMS door resolves it. The list and the count ask the server
 * the same question (`RecipientsProvider` / `MetaTableProvider`: the flat
 * rows or the tree, plus the whitelist and the blacklist), and the door
 * builds its set with the same `buildGuestMatchCondition`
 * (`GuestRepository.smsManualSelectionMatch`, active list ANDed around it):
 *
 *  - explicit picks ⇒ `ids` = the whitelist, sorted (the list shows exactly
 *    the whitelist; the blacklist plays no part there);
 *  - "all" ⇒ only under a filter whose flat rows mean the list's filter
 *    (`allMatchingFilterIsSafe`, Teil A: the door takes flat rows only, never
 *    the tree) — else refused. The flat rows go through the parser keeping an
 *    unresolved key, so the door answers it with 400 instead of a wider set;
 *    an `archived` row may only say "active": the door reads the active list
 *    itself, so such a row is dropped — any other `archived` row (e.g.
 *    "Archiviert = ja") would be dropped into "every active guest" while the
 *    list shows nobody, and is refused (audit B1 MED-1 / LOW-2). A FLAT row
 *    says "active" only as `= false`: the list's flat lane reads the first
 *    `archived` row's VALUE and ignores its operator (main-backend
 *    GuestResource `/paginated`), so `≠ true` there is the ARCHIVED list. A
 *    tree leaf (anywhere in the tree) may also be `!= true`: the tree lane
 *    compiles the operator (`$ne`). So "Archiviert ≠ ja" as PFilter writes
 *    it (tree `!= true` + its flat twin `≠ "true"`) is refused — on the safe
 *    side (audit B2 LOW-1 / LOW-2);
 *  - "all" + extra picks under a non-empty filter ⇒ the picks not removed
 *    again (`includeIds` = whitelist − blacklist) beside the filter, the
 *    removals as `excludeIds`: (filter ∧ ¬blacklist) ∨ (picks ∧ ¬blacklist);
 *  - "all" + extra picks with NO filter rows: the server's rule "whitelist
 *    without filters ⇒ exactly those ids" decides what the list shows — on the
 *    flat lane the list shows only the picks, so the send is the picks
 *    (`ids` = whitelist − blacklist); a tree with at least one leaf whose flat
 *    rows are empty (only "active" `archived` leaves) is "every guest" on the
 *    list (the tree makes it a union), so the picks are already inside "all"
 *    and the whitelist is dropped (HIGH-3); a tree WITHOUT leaves (empty
 *    groups) compiles to nothing on the server, so it reads as the flat lane
 *    (picks only) — audit B1 MED-1;
 *  - the blacklist always wins over the whitelist, as on the server.
 *
 * Every list is sorted, so the send's signature (and its idempotency key)
 * does not depend on the order of the clicks (LOW-5). The "Weiter" check
 * (count gate) is the belt: the door's `selected` must equal the picker's
 * count, or nothing opens.
 */
/** `false` / `true` as a boolean or its string, any case. */
const archivedValueIs = (value: unknown, want: boolean): boolean =>
  value === want || (typeof value === "string" && value.toLowerCase() === String(want));

/**
 * A FLAT `archived` row that means "the active list": only `= false`. The
 * list's flat lane reads the first `archived` row's value and ignores its
 * operator, so `≠ true` there would be the archived list (audit B2 LOW-1).
 */
function archivedFlatRowMeansActive(row: { operator?: unknown; value?: unknown }): boolean {
  return row.operator === "=" && archivedValueIs(row.value, false);
}

/** A tree leaf on `archived` that means "the active list": `= false` or `!= true` (the tree lane compiles the operator). */
function archivedLeafMeansActive(row: { operator?: unknown; value?: unknown }): boolean {
  if (row.operator === "=") return archivedValueIs(row.value, false);
  if (row.operator === "!=") return archivedValueIs(row.value, true);
  return false;
}

/** Every `archived` row — the flat ones and the tree's leaves at any depth — means "the active list". */
function archivedRowsMeanActive(state: { filter?: Filter[]; conditions?: ConditionGroup } | null | undefined): boolean {
  const flatOk = (state?.filter ?? []).every((r) => r?.key !== "archived" || archivedFlatRowMeansActive(r));
  const treeOk = (g: ConditionGroup | undefined): boolean => {
    if (!g || typeof g !== "object" || Array.isArray(g)) return true;
    return (
      (g.filters ?? []).every((f) => f?.key !== "archived" || archivedLeafMeansActive(f)) &&
      (g.groups ?? []).every((sub) => treeOk(sub))
    );
  };
  return flatOk && treeOk(state?.conditions);
}

export function pickerListsToSelection(
  lists: SmsPickerLists,
  filterState: { filter?: Filter[]; conditions?: ConditionGroup } | null | undefined,
  allFieldsUnfiltered: Field[] | undefined,
): SmsPickerSelection {
  const whitelist = sortedUnique(lists.whitelist);
  if (!lists.allRecipients) {
    return whitelist.length > 0 ? { ok: true, selection: { mode: "ids", ids: whitelist } } : { ok: false, reason: "empty" };
  }
  if (!allMatchingFilterIsSafe(filterState, allFieldsUnfiltered, "guest")) return { ok: false, reason: "filter" };
  if (!archivedRowsMeanActive(filterState)) return { ok: false, reason: "filter" };
  const flat = (filterState?.filter ?? []).filter((r) => r?.key !== "archived").map((r) => ({ ...r }));
  const filters = parseFiltersApiRequest(flat, allFieldsUnfiltered ?? [], { onUnresolvedKey: "keep" });
  const blacklist = sortedUnique(lists.blacklist);
  const removed = new Set(blacklist);
  const picks = whitelist.filter((id) => !removed.has(id));
  const all = { mode: "filter" as const, excludeIds: blacklist, search: "", archived: false, selectAll: true };
  if (filters.length > 0) {
    return { ok: true, selection: { ...all, filters, ...(picks.length > 0 ? { includeIds: picks } : {}) } };
  }
  if (whitelist.length === 0 || countConditionFilters(filterState?.conditions) > 0) {
    return { ok: true, selection: { ...all, filters: [] } };
  }
  return picks.length > 0 ? { ok: true, selection: { mode: "ids", ids: picks } } : { ok: false, reason: "empty" };
}

// ── One rule per guest: the number an SMS goes to, or why not (M1) ──────────

export type SmsContactLine = { ok: true; e164: string } | { ok: false; skipKey: SmsSkipReasonKey };

/**
 * What an SMS list says under a guest's name — the picker's rows and the
 * dialog's guest box read this ONE rule, so they agree per guest: no opt-in
 * ⇒ "no SMS opt-in"; else the mobile first, then the phone, a DACH mobile
 * only (`pickSmsNumber`, never a landline) ⇒ the number; else why not.
 */
export function smsContactLineOf(guest: Pick<Guest, "smsOptIn" | "mobile" | "phone">): SmsContactLine {
  if (guest.smsOptIn !== true) return { ok: false, skipKey: "optOut" };
  const pick = pickSmsNumber(guest.mobile, guest.phone);
  if (pick.verdict === "reachable" && pick.e164) return { ok: true, e164: pick.e164 };
  const key = pick.skip ? smsSkipReasonKey(pick.skip) : null;
  return { ok: false, skipKey: key ?? "noPhone" };
}

// ── The text ────────────────────────────────────────────────────────────────

export type SmsManualText =
  | { kind: "own"; body: string }
  | { kind: "template"; template: Pick<SmsTemplate, "smsTemplateId" | "version" | "primaryLanguage" | "languages" | "bodies"> };

/** The text a guest of `guestLanguage` gets, and its language (the template's fallback chain). */
export function smsManualTextFor(text: SmsManualText, guestLanguage: string): { text: string; lang: string } {
  if (text.kind === "own") return { text: text.body, lang: guestLanguage };
  const tp = text.template;
  return smsTextForGuest(tp.bodies, [tp.primaryLanguage, ...tp.languages], tp.primaryLanguage, guestLanguage);
}

// ── M4: the cost line ───────────────────────────────────────────────────────

/** A guest's value standing in for each guest marker in the "may not suffice" sample: 40 characters outside GSM-7. */
const UCS2_SAMPLE = "Ł".repeat(40);
const ANY_MARKER = /\{\{[^{}]*\}\}/g;

export interface SmsManualCost {
  /** "ca. X SMS parts": per language, its recipients × the parts of its text with the event's values. */
  estimate: number;
  /** "at most Y" = reachable × 3 — a true bound. */
  max: number;
  /** The same sum with every guest marker as a 40-character UCS-2 value — drives "may not suffice". */
  sample: number;
}

/**
 * The cost of a send from the server's per-language counts (F3-17): each
 * language's recipients × the parts of the text they get. Counts missing
 * from `byLanguage` (an older server, a capped check) are priced at the
 * main language.
 */
export function smsManualCostOf(
  byLanguage: Readonly<Record<string, number>>,
  reachable: number,
  text: SmsManualText,
  valuesFor: (lang: string) => SmsEventValues,
): SmsManualCost {
  const groups: Array<[string, number]> = Object.entries(byLanguage).filter(([, n]) => n > 0);
  const counted = groups.reduce((s, [, n]) => s + n, 0);
  if (reachable > counted) groups.push(["", reachable - counted]);
  let estimate = 0;
  let sample = 0;
  for (const [guestLang, n] of groups) {
    const { text: body, lang } = smsManualTextFor(text, guestLang);
    const filled = fillSmsEventMarkers(body, valuesFor(lang));
    const parts = Math.max(1, measureSms(filled).parts);
    const sampleParts = Math.max(1, measureSms(filled.replace(ANY_MARKER, UCS2_SAMPLE)).parts);
    estimate += n * Math.min(parts, SMS_MAX_PARTS);
    sample += n * Math.min(sampleParts, SMS_MAX_PARTS);
  }
  return { estimate, max: reachable * SMS_MAX_PARTS, sample };
}

/** "none" | "weak" (may not suffice) | "strong" (does not suffice), against the allowance left. */
export function smsManualCostWarning(cost: SmsManualCost, remaining: number | undefined): "none" | "weak" | "strong" {
  if (remaining === undefined) return "none";
  if (cost.estimate > remaining) return "strong";
  if (cost.sample > remaining) return "weak";
  return "none";
}

// ── F3-5 / F3-16: what an answer means ──────────────────────────────────────

/**
 * Codes after which asking again cannot help — Send stays disabled with the
 * manual-send reason, no Retry (F3-5). The reason lives at
 * `smsManualSend.reasons.<code>`; every other code goes through `errors`.
 */
export const SMS_MANUAL_DEFINITIVE_CODES: readonly string[] = [
  "insufficientCapability",
  "smsManualCompanyOnly",
  "smsChannelDisabled",
  "smsPluginDisabled",
  "smsNotEnabledForCompany",
  "eventArchived",
  "eventPlanLocked",
];

/** The submit's 429s of the day limits (event 1 000, company 2 000; Retry-After = next UTC midnight). */
export const SMS_MANUAL_DAY_LIMIT_CODES: readonly string[] = ["smsManualDailyLimit", "smsManualCompanyDailyLimit"];

/** The submit's 409s that mean "look again": the audience or the template moved since the check. */
export const SMS_MANUAL_ASK_AGAIN_CODES: readonly string[] = ["smsManualAudienceChanged", "smsTemplateChanged"];

/**
 * How a FAILED send ended when NOTHING was sent: the fan-out's refusals before
 * the first guest (main-consumer `SmsManualJobFailure`: switch, plugin,
 * allowance, the day limits, the envelope) and the backend's two late
 * producer failures, "could not start" and "start unknown". The job tray then
 * does not ask to check what went out, and the per-send view says "did not
 * start" instead of "0 sent".
 *
 * `smsManualStartUnknown` belongs here (audit r2 R2-FE-1): the backend writes
 * it only through a CAS on "PENDING, no fan-out marker"
 * (`BackgroundJobRepository.failSmsManualBeforeFanout`), and the consumer
 * starts a fan-out only through the mirror CAS "not terminal, no fan-out
 * marker" (`sms-manual-fanout.ts`) — once the job is FAILED with it, its
 * fan-out can never start. "Unknown" was about the queue message, not the send.
 */
export const SMS_MANUAL_NOTHING_SENT_CODES: readonly string[] = [
  "smsManualChannelOff",
  "smsManualPluginOff",
  "smsManualNotEnabled",
  "smsManualNoAllowance",
  "smsManualDailyLimit",
  "smsManualCompanyDailyLimit",
  "smsManualNoRecipients",
  "smsManualTooManyRecipients",
  "smsManualCouldNotStart",
  "smsManualStartUnknown",
];

/**
 * The code whose words say how a FAILED send ended, for the tray and the
 * per-send view. `smsManualStartUnknown`'s own `errors.*` words ("may not have
 * started — check Sent SMS before you send again") speak to the open dialog;
 * on a FAILED job the start failed for certain (see above), which is what
 * `smsManualCouldNotStart` says: could not start, nothing was sent (R2-FE-1).
 */
export function smsManualEndWordsCode(code: string): string {
  return code === "smsManualStartUnknown" ? "smsManualCouldNotStart" : code;
}

/**
 * The per-send view's end reason for a job that has NOT STARTED: PENDING, no
 * fan-out marker, older than an hour (main-backend
 * `BackgroundJobRepository.isUnstartedSmsManual`); the job's stored status
 * stays PENDING (audit r2 FE-5). A guess from the clock, not an end: a fan-out
 * message that is only late can still start the job (the consumer's start CAS
 * takes any job that is not over and has no marker), and a resend beside it
 * would send — and bill — every SMS twice. So the view says nothing was sent
 * SO FAR, keeps reading itself again (slowly, and on focus) so a late start
 * shows "running", and offers the cancel on its card: only after the cancel
 * may the send go again (audit r3 R3-1).
 * MIRROR: main-backend `SentSmsService.END_NOT_STARTED`.
 */
export const SMS_MANUAL_NOT_STARTED_CODE = "smsManualNotStarted";

/**
 * The per-send view's end reason for a job whose fan-out message died (the
 * backend's code for the dlq's `fan-out failed: …`, never that raw text).
 * MIRROR: main-backend `SentSmsService.END_FANOUT_FAILED`.
 */
export const SMS_MANUAL_FANOUT_DEAD_CODE = "smsManualFanoutFailed";

/**
 * The per-send view's end reason for any other text on the job (main-backend
 * `SentSmsService.END_UNKNOWN`): the view says only that the send failed.
 */
export const SMS_MANUAL_END_UNKNOWN_CODE = "smsManualEndUnknown";

/**
 * A job whose fan-out message died (dlq-consumer `markJobFailedFanout`
 * writes `fan-out failed: <the consumer's error>`): internal text, never
 * shown — the send broke off while it was being handed out to the guests.
 */
export function isSmsManualDeadFanout(errorMessage: string | null | undefined): boolean {
  return typeof errorMessage === "string" && errorMessage.startsWith("fan-out failed:");
}

type ErrorLike =
  | { message?: unknown; httpStatus?: unknown; status?: unknown; retryAfter?: unknown; local?: unknown }
  | null
  | undefined;

export type SmsManualAnswer =
  /**
   * No HTTP status (network, abort, timeout) or any 5xx: the request may or
   * may not have landed. A refusal of the client's own checks before any
   * request (`local`) is never this — nothing left the browser.
   */
  | { kind: "unclear" }
  | { kind: "rateLimited"; code: string; retryAfterSeconds: number | null }
  | { kind: "askAgain"; code: string }
  | { kind: "definitive"; code: string }
  /** Any other refusal: said in words, the dialog stays usable. */
  | { kind: "refused"; code: string };

const statusOf = (e: ErrorLike): number | null => {
  const s = e?.httpStatus ?? e?.status;
  return typeof s === "number" && Number.isFinite(s) ? s : null;
};

export function classifySmsManualError(error: ErrorLike): SmsManualAnswer {
  const status = statusOf(error);
  const code = typeof error?.message === "string" && error.message !== "" ? error.message : "unknown";
  // The handler refused the body before the wire (audit r1 C9 / LOW-5): said
  // as a refusal, the dialog stays closable — "status unclear" would offer
  // only the same request again, refused the same way for ever.
  if (error?.local === true) return { kind: "refused", code };
  if (status === null || status >= 500) return { kind: "unclear" };
  if (status === 429) {
    const ra = error?.retryAfter;
    return { kind: "rateLimited", code, retryAfterSeconds: typeof ra === "number" && ra > 0 ? ra : null };
  }
  if (SMS_MANUAL_ASK_AGAIN_CODES.includes(code)) return { kind: "askAgain", code };
  if (SMS_MANUAL_DEFINITIVE_CODES.includes(code)) return { kind: "definitive", code };
  return { kind: "refused", code };
}

/** The next UTC midnight (the day limits' reset), as an instant. */
export function nextUtcMidnight(now: Date = new Date()): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1));
}

// ── F3-2: the submit key that outlives the dialog ───────────────────────────

/**
 * `localStorage`, one entry for every send key of this browser, keyed by a
 * HASH of the send's signature — never a guest id, a filter or a text in
 * clear (the e2e storage PII scan). An entry lives 10 minutes; the account
 * teardown sweeps the key with every other account-bound one, and the
 * listener below drops it in memory too.
 */
export const SMS_MANUAL_KEYS_STORAGE = "veltric.smsManualSendKeys";
export const SMS_MANUAL_KEY_TTL_MS = 10 * 60_000;

/**
 * `u: 1` marks a key whose answer was lost ("status unclear", audit r2
 * R2-FE-4): that first request may still land, so a refusal never retires
 * the key — only a 2xx or its 10 minutes do, in any dialog of this browser.
 */
type StoredKeys = Record<string, { k: string; t: number; u?: 1 }>;

/** The send's identity — everything that changes what is sent, minus the send name and the confirmed count. */
export function smsManualSignature(input: {
  eventId: string;
  selection: SmsManualSelectionBody;
  text: { kind: "own"; body: string } | { kind: "template"; smsTemplateId: string; version: number | null };
  markAsContacted: boolean;
}): string {
  return smsManualHash(stableStringify(input));
}

/** Two 53-bit string hashes (cyrb53), hex — an index, not a secret. */
export function smsManualHash(s: string): string {
  const one = (seed: number) => {
    let h1 = 0xdeadbeef ^ seed;
    let h2 = 0x41c6ce57 ^ seed;
    for (let i = 0; i < s.length; i++) {
      const ch = s.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }
    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
    return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16).padStart(14, "0");
  };
  return one(1) + one(2);
}

function readKeys(now: number): StoredKeys {
  try {
    const raw = localStorage.getItem(SMS_MANUAL_KEYS_STORAGE);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const out: StoredKeys = {};
    for (const [h, v] of Object.entries(parsed as Record<string, unknown>)) {
      const e = v as { k?: unknown; t?: unknown; u?: unknown } | null;
      if (
        e && typeof e.k === "string" && /^[A-Za-z0-9._:-]{1,64}$/.test(e.k) &&
        typeof e.t === "number" && now - e.t >= 0 && now - e.t < SMS_MANUAL_KEY_TTL_MS
      ) {
        out[h] = e.u === 1 ? { k: e.k, t: e.t, u: 1 } : { k: e.k, t: e.t };
      }
    }
    return out;
  } catch {
    return {};
  }
}

function writeKeys(keys: StoredKeys): void {
  try {
    if (Object.keys(keys).length === 0) localStorage.removeItem(SMS_MANUAL_KEYS_STORAGE);
    else localStorage.setItem(SMS_MANUAL_KEYS_STORAGE, JSON.stringify(keys));
  } catch {
    /* storage unavailable: the key lives as long as the dialog */
  }
}

function mintKey(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 12);
}

/**
 * The idempotency key of this send: the stored one while it is younger than
 * 10 minutes (a reopened dialog, a reload after a lost answer), else a fresh
 * one, stored. The 10 minutes count from the FIRST use.
 */
export function smsManualSubmitKey(signature: string, now: number = Date.now()): string {
  const keys = readKeys(now);
  const hit = keys[signature];
  if (hit) {
    writeKeys(keys);
    return hit.k;
  }
  const k = mintKey();
  keys[signature] = { k, t: now };
  writeKeys(keys);
  return k;
}

/** Retire the key: a 2xx (new job or replay) — the mark of a lost answer included. */
export function retireSmsManualSubmitKey(signature: string, now: number = Date.now()): void {
  const keys = readKeys(now);
  delete keys[signature];
  writeKeys(keys);
}

/**
 * The answer to this send's request was lost ("status unclear"): mark its
 * stored key, so no refusal retires it — also after the dialog was closed and
 * opened again (R2-FE-4). A send without a stored key stores nothing (the
 * mark never starts a new 10 minutes).
 */
export function markSmsManualSubmitKeyUnclear(signature: string, now: number = Date.now()): void {
  const keys = readKeys(now);
  const hit = keys[signature];
  if (!hit) return;
  keys[signature] = { ...hit, u: 1 };
  writeKeys(keys);
}

/**
 * A refusal created nothing: retire the key, so the next attempt is a new
 * request — unless an earlier attempt of this send lost its answer (marked):
 * that one may still land, so its key stays for a 2xx or its 10 minutes.
 */
export function releaseSmsManualSubmitKey(signature: string, now: number = Date.now()): void {
  const keys = readKeys(now);
  if (!keys[signature] || keys[signature].u === 1) return;
  delete keys[signature];
  writeKeys(keys);
}

if (typeof window !== "undefined") {
  window.addEventListener(ACCOUNT_TEARDOWN_EVENT, () => {
    try {
      localStorage.removeItem(SMS_MANUAL_KEYS_STORAGE);
    } catch {
      /* nothing stored */
    }
  });
}
